import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
    pending: { color: "#eab308", label: "Pending" },
    recovered: { color: "#3b82f6", label: "Recovery Sent" },
    completed: { color: "#22c55e", label: "Converted" },
    expired: { color: "#71717a", label: "Expired" },
};

export default function AbandonedCartsTab({ password }) {
    const [carts, setCarts] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const url = filter === "all"
                ? `${API}/admin/abandoned-carts`
                : `${API}/admin/abandoned-carts?status=${filter}`;
            const res = await axios.get(url, { headers: { "X-Admin-Password": password } });
            setCarts(res.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load carts");
        } finally {
            setLoading(false);
        }
    }, [filter, password]);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(() => ({
        pending: carts.filter((c) => c.status === "pending").length,
        recovered: carts.filter((c) => c.status === "recovered").length,
        completed: carts.filter((c) => c.status === "completed").length,
    }), [carts]);

    const runNow = async () => {
        setProcessing(true);
        try {
            const res = await axios.post(
                `${API}/admin/abandoned-carts/process-now`,
                null,
                { headers: { "X-Admin-Password": password } }
            );
            const { sent, failed, skipped } = res.data;
            toast.success(`Sent ${sent} · Skipped ${skipped} · Failed ${failed}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Process failed");
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div data-testid="abandoned-tab" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    {[
                        { k: "pending", label: "Pending", count: counts.pending },
                        { k: "recovered", label: "Recovery Sent", count: counts.recovered },
                        { k: "completed", label: "Converted", count: counts.completed },
                        { k: "all", label: "All", count: carts.length },
                    ].map((f) => {
                        const active = filter === f.k;
                        return (
                            <button
                                key={f.k}
                                data-testid={`abandoned-filter-${f.k}`}
                                onClick={() => setFilter(f.k)}
                                className={`text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border ${
                                    active
                                        ? "bg-[#E63946] border-[#E63946] text-white"
                                        : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                                }`}
                            >
                                {f.label} <span className="opacity-70">({f.count})</span>
                            </button>
                        );
                    })}
                </div>
                <button
                    data-testid="abandoned-process-now"
                    onClick={runNow}
                    disabled={processing}
                    className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-2 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white disabled:opacity-50"
                    title="Force-run recovery emails for any carts older than 60 minutes"
                >
                    {processing ? "Running…" : "↻ Run Recovery Now"}
                </button>
            </div>

            {loading && <div className="text-zinc-500 text-sm py-12 text-center">Loading…</div>}
            {!loading && carts.length === 0 && (
                <div className="bg-fgm-surface border border-white/10 p-12 text-center text-zinc-500 font-display tracking-widest uppercase">
                    No abandoned carts in this view
                </div>
            )}

            {!loading && carts.length > 0 && (
                <div className="bg-fgm-surface border border-white/10 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-black/40 border-b border-white/10">
                            <tr>
                                <th className="text-left px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Email</th>
                                <th className="text-left px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Items</th>
                                <th className="text-left px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Captured</th>
                                <th className="text-left px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Status</th>
                                <th className="text-left px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Recovery Code</th>
                            </tr>
                        </thead>
                        <tbody>
                            {carts.map((c) => {
                                const badge = STATUS_BADGE[c.status] || { color: "#71717a", label: c.status };
                                return (
                                    <tr key={c.id} data-testid={`abandoned-row-${c.id}`} className="border-b border-white/5">
                                        <td className="px-4 py-3 text-white font-mono text-xs">{c.email}</td>
                                        <td className="px-4 py-3 text-zinc-400 text-xs">
                                            {(c.items || []).slice(0, 3).map((i) => `${i.name || "Item"} ×${i.quantity || i.qty || 1}`).join(", ")}
                                            {(c.items || []).length > 3 && ` +${c.items.length - 3} more`}
                                        </td>
                                        <td className="px-4 py-3 text-zinc-500 text-xs whitespace-nowrap">
                                            {c.captured_at ? new Date(c.captured_at).toLocaleString() : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className="text-[10px] font-display tracking-[0.25em] uppercase px-2 py-1 border whitespace-nowrap"
                                                style={{ color: badge.color, borderColor: `${badge.color}80` }}
                                            >
                                                ● {badge.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-zinc-400 font-mono text-xs">
                                            {c.recovery_code || "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
